// src/lib/problemBankAccess.ts
import { supabase } from './supabaseClient';
import { ProblemBankEntry, ExternalSubmission } from '@/types/problemBank';

// ==============================================
// USER ROLE UTILITIES - UPDATED for SquadAuth
// ==============================================

export type UserRole = 'admin' | 'analyst' | 'squad_member' | 'company' | 'public';

// Helper to check if user is authenticated via SquadAuth (localStorage)
function isSquadAuthenticated(): boolean {
  const squadSession = localStorage.getItem('squad-session');
  const forgeAuth = localStorage.getItem('forge-squad-auth');
  const currentSquad = localStorage.getItem('current-squad');
  
  return !!(squadSession || forgeAuth === 'true' || currentSquad);
}

// Get squad user from localStorage if available
function getSquadUserFromLocalStorage() {
  const squadSession = localStorage.getItem('squad-session');
  if (squadSession) {
    try {
      return JSON.parse(squadSession);
    } catch (e) {
      return null;
    }
  }
  const currentSquad = localStorage.getItem('current-squad');
  if (currentSquad) {
    try {
      return JSON.parse(currentSquad);
    } catch (e) {
      return null;
    }
  }
  return null;
}

export async function getCurrentUserRole(): Promise<UserRole | null> {
  // FIRST: Check if user is authenticated via SquadAuth (localStorage)
  if (isSquadAuthenticated()) {
    console.log('✅ Squad authenticated via localStorage, returning squad_member');
    return 'squad_member';
  }
  
  // SECOND: Check Supabase Auth for analysts/admins
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.log('❌ No authenticated user found in Supabase or localStorage');
    return null;
  }
  
  const { data } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();
    
  return data?.role || null;
}

export async function getCurrentUserProfile() {
  // FIRST: Check SquadAuth localStorage
  const squadUser = getSquadUserFromLocalStorage();
  if (squadUser && squadUser.squadId) {
    console.log('✅ Returning squad profile from localStorage');
    return {
      id: squadUser.memberId || squadUser.id,
      squad_id: squadUser.squadId,
      role: 'squad_member',
      full_name: squadUser.memberName || squadUser.name,
      email: squadUser.memberEmail || squadUser.email
    };
  }
  
  // SECOND: Fall back to Supabase
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  
  const { data } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();
    
  return data;
}

// ==============================================
// ADMIN ACCESS (Full control)
// ==============================================

export const adminAccess = {
  async getAllUsers() {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    return data;
  },
  
  async updateUserRole(userId: string, role: UserRole) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ role })
      .eq('id', userId)
      .select()
      .single();
      
    if (error) throw error;
    return data;
  },
  
  async deleteProblem(problemId: string) {
    const { error } = await supabase
      .from('problem_bank')
      .delete()
      .eq('id', problemId);
      
    if (error) throw error;
  },
  
  async getSystemAnalytics() {
    const [problems, users, submissions] = await Promise.all([
      supabase.from('problem_bank').select('*'),
      supabase.from('user_profiles').select('*'),
      supabase.from('external_submissions').select('*')
    ]);
    
    return {
      totalProblems: problems.data?.length || 0,
      totalUsers: users.data?.length || 0,
      totalSubmissions: submissions.data?.length || 0,
      problemsByStatus: problems.data?.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    };
  }
};

// ==============================================
// ANALYST ACCESS (Verify, create, assign)
// ==============================================

export const analystAccess = {
  async createProblem(problemData: Omit<ProblemBankEntry, 'id' | 'createdAt' | 'updatedAt'>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase
      .from('problem_bank')
      .insert([{
        ...problemData,
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();
      
    if (error) throw error;
    return data;
  },
  
  async updateProblem(problemId: string, updates: Partial<ProblemBankEntry>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase
      .from('problem_bank')
      .update({
        ...updates,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      })
      .eq('id', problemId)
      .select()
      .single();
      
    if (error) throw error;
    return data;
  },
  
  async verifyProblem(problemId: string, verificationData: any) {
    return this.updateProblem(problemId, {
      status: 'verified',
      verification: verificationData
    });
  },
  
  async assignToSquad(problemId: string, squadId: string, assignmentData: any) {
    return this.updateProblem(problemId, {
      status: 'assigned',
      assignment: {
        squadId,
        ...assignmentData,
        assignmentDate: new Date().toISOString()
      }
    });
  },
  
  async markAsSolved(problemId: string, outcomeData: any) {
    return this.updateProblem(problemId, {
      status: 'solved',
      outcome: outcomeData
    });
  },
  
  async calculatePriorityScore(problemId: string) {
    const problem = await analystAccess.getProblem(problemId);
    if (!problem) throw new Error('Problem not found');
    
    const score = calculatePriorityScore(problem);
    
    return this.updateProblem(problemId, {
      priorityScore: score
    });
  },
  
  async getPendingSubmissions() {
    const { data, error } = await supabase
      .from('external_submissions')
      .select('*')
      .eq('status', 'pending')
      .order('submitted_at', { ascending: true });
      
    if (error) throw error;
    return data;
  },
  
  async reviewSubmission(submissionId: string, decision: 'accepted' | 'rejected', notes: string, convertedProblemId?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('external_submissions')
      .update({
        status: decision,
        review_notes: notes,
        converted_to_problem_id: convertedProblemId,
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', submissionId)
      .select()
      .single();
      
    if (error) throw error;
    return data;
  },
  
  async getProblems(filters?: any) {
    let query = supabase.from('problem_bank').select('*');
    
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.priority) {
      query = query.eq('priority_score->>priorityTier', filters.priority);
    }
    if (filters?.minLoss) {
      query = query.gte('quantification->>totalLossPerYear', filters.minLoss);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },
  
  async getProblem(problemId: string) {
    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .eq('id', problemId)
      .single();
      
    if (error) throw error;
    return data;
  }
};

// ==============================================
// SQUAD MEMBER ACCESS (Browse, express interest)
// ==============================================

export const squadAccess = {
  // Browse available problems (Level 3 verified)
  async getAvailableProblems() {
    console.log('🔍 squadAccess.getAvailableProblems() called');
    
    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .eq('status', 'verified')
      .order('priority_score->>totalScore', { ascending: false });
      
    if (error) {
      console.error('❌ Supabase error:', error);
      throw error;
    }
    
    console.log(`✅ Found ${data?.length || 0} verified problems`);
    if (data && data.length > 0) {
      console.log('📊 First problem:', { 
        id: data[0].id, 
        title: data[0].title, 
        status: data[0].status 
      });
    }
    
    return data;
  },
  
  async searchProblems(query: string) {
    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .textSearch('search_vector', query)
      .limit(20);
      
    if (error) throw error;
    return data;
  },
  
  async getProblemDetails(problemId: string) {
    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .eq('id', problemId)
      .single();
      
    if (error) throw error;
    return data;
  },
  
  // FIXED: Use correct squad_member_id from squad_members table
  async expressInterest(problemId: string, message: string) {
    const profile = await getCurrentUserProfile();
    
    if (!profile?.squad_id) {
      throw new Error('You must be part of a squad to express interest');
    }
    
    if (!profile?.email) {
      throw new Error('Could not determine your email address');
    }
    
    // Get the squad member ID from the squad_members table using email
    const { data: squadMember, error: squadMemberError } = await supabase
      .from('squad_members')
      .select('id')
      .eq('squad_id', profile.squad_id)
      .eq('email', profile.email)
      .single();
      
    if (squadMemberError || !squadMember) {
      console.error('❌ Error finding squad member:', squadMemberError);
      throw new Error('Could not find your squad member record. Please ensure you are logged in as a squad member.');
    }
    
    console.log('📝 Expressing interest for problem:', problemId);
    console.log('👥 Squad ID:', profile.squad_id);
    console.log('👤 Squad Member ID:', squadMember.id);
    console.log('💬 Message:', message);
    
    const { data, error } = await supabase
      .from('squad_problem_interests')
      .insert([{
        problem_id: problemId,
        squad_id: profile.squad_id,
        squad_member_id: squadMember.id,
        reason: message,
        status: 'pending',
        expressed_at: new Date().toISOString()
      }])
      .select();
      
    if (error) {
      console.error('❌ Error inserting interest:', error);
      throw error;
    }
    
    console.log('✅ Interest expressed successfully:', data);
    return data;
  },
  
  async getMySquadInterests() {
    const profile = await getCurrentUserProfile();
    if (!profile?.squad_id) return [];
    
    const { data, error } = await supabase
      .from('squad_problem_interests')
      .select(`
        *,
        problem:problem_id (*)
      `)
      .eq('squad_id', profile.squad_id)
      .order('expressed_at', { ascending: false });
      
    if (error) throw error;
    return data;
  },
  
  async getMyAssignedProblems() {
    const profile = await getCurrentUserProfile();
    if (!profile?.squad_id) return [];
    
    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .eq('status', 'assigned')
      .eq('assignment->>squadId', profile.squad_id);
      
    if (error) throw error;
    return data;
  }
};

// ==============================================
// COMPANY ACCESS (Submit problems, track submissions)
// ==============================================

export const companyAccess = {
  async submitProblem(submission: Omit<ExternalSubmission, 'id' | 'submittedAt' | 'status'>) {
    const { data, error } = await supabase
      .from('external_submissions')
      .insert([{
        ...submission,
        status: 'pending',
        submitted_at: new Date().toISOString()
      }])
      .select()
      .single();
      
    if (error) throw error;
    return data;
  },
  
  async getMySubmissions() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase
      .from('external_submissions')
      .select('*')
      .eq('contact_email', user.email)
      .order('submitted_at', { ascending: false });
      
    if (error) throw error;
    return data;
  },
  
  async getSubmissionStatus(submissionId: string) {
    const { data, error } = await supabase
      .from('external_submissions')
      .select('status, review_notes, converted_to_problem_id')
      .eq('id', submissionId)
      .single();
      
    if (error) throw error;
    return data;
  }
};

// ==============================================
// PUBLIC ACCESS (View only)
// ==============================================

export const publicAccess = {
  async getPublicProblems() {
    const { data, error } = await supabase
      .from('problem_bank')
      .select(`
        id,
        problem_id,
        title,
        industry_sector,
        quantification->totalLossPerYear,
        solution_potential->marketSizeKenya
      `)
      .eq('status', 'verified')
      .limit(50);
      
    if (error) throw error;
    return data;
  },
  
  async getProblemStats() {
    const { data, error } = await supabase
      .from('problem_bank')
      .select('industry_sector, status');
      
    if (error) throw error;
    
    const stats = {
      total: data.length,
      byIndustry: {} as Record<string, number>,
      byStatus: {} as Record<string, number>
    };
    
    data.forEach(p => {
      stats.byIndustry[p.industry_sector] = (stats.byIndustry[p.industry_sector] || 0) + 1;
      stats.byStatus[p.status] = (stats.byStatus[p.status] || 0) + 1;
    });
    
    return stats;
  }
};

// ==============================================
// HELPER FUNCTIONS
// ==============================================

function calculatePriorityScore(problem: ProblemBankEntry) {
  const lossScore = scoreLossMagnitude(problem.quantification.totalLossPerYear);
  const urgencyScore = problem.verification?.anchorUrgency || 3;
  const feasibilityScore = problem.solutionPotential?.scalabilityScore || 3;
  const marketScore = scoreMarketSize(problem.solutionPotential?.marketSizeKenya || 0);
  const relationshipScore = problem.anchor?.relationshipStrength || 3;
  
  const totalScore = (
    (lossScore * 0.30) +
    (urgencyScore * 0.25) +
    (feasibilityScore * 0.20) +
    (marketScore * 0.15) +
    (relationshipScore * 0.10)
  );
  
  let priorityTier: 'critical' | 'high' | 'medium' | 'low';
  if (totalScore >= 4.0) priorityTier = 'critical';
  else if (totalScore >= 3.0) priorityTier = 'high';
  else if (totalScore >= 2.0) priorityTier = 'medium';
  else priorityTier = 'low';
  
  return {
    lossMagnitude: { score: lossScore, annualLoss: problem.quantification.totalLossPerYear },
    anchorUrgency: { score: urgencyScore, description: '' },
    solutionFeasibility: { score: feasibilityScore, description: '' },
    marketSize: { score: marketScore, customerCount: problem.solutionPotential?.marketSizeKenya || 0 },
    relationshipStrength: { score: relationshipScore, description: '' },
    totalScore,
    priorityTier,
    calculatedAt: new Date().toISOString(),
    calculatedBy: 'system'
  };
}

function scoreLossMagnitude(annualLoss: number): 1 | 2 | 3 | 4 | 5 {
  if (annualLoss < 3000000) return 1;
  if (annualLoss < 6000000) return 2;
  if (annualLoss < 10000000) return 3;
  if (annualLoss < 15000000) return 4;
  return 5;
}

function scoreMarketSize(customerCount: number): 1 | 2 | 3 | 4 | 5 {
  if (customerCount < 5) return 1;
  if (customerCount < 10) return 2;
  if (customerCount < 20) return 3;
  if (customerCount < 50) return 4;
  return 5;
}